import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {TransportAssocEntity} from "@ots-frontend/ots-data";
import {IconCircleCheck, IconDownload, IconEdit, IconTrash} from "@tabler/icons-react";
import {useTicketAction} from "./useTicketAction";

const ALL_TICKET_ACTIONS = [
  "DOWNLOAD",
  "EDIT",
  "TOGGLE_PICKUP_DEMURRAGE",
  "TOGGLE_DELIVERY_DEMURRAGE",
  "TOGGLE_DISPATCH_VALIDATION",
  "DELETE",
  "DELETE_ASSOC"
] as const;

export type TicketAction = typeof ALL_TICKET_ACTIONS[number];

export type TicketActionMenuDropdownProps = {
  ticket: TransportAssocEntity
  actions?: TicketAction[]
  onActionClick?: (action: TicketAction) => void
  showEmpty?: boolean
}

export const TicketActionMenuDropdown = (props: TicketActionMenuDropdownProps) => {
  const {
    ticket,
    actions = ALL_TICKET_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<TicketAction> = useMemo(() => {
    const enabledActions = new Set<TicketAction>()
    const actionsSet = actions ? new Set<TicketAction>(actions) : new Set<TicketAction>()

    if (hasAccess("transporttransportassoc_edit")) {
        if (actionsSet.has("EDIT")) {
          enabledActions.add("EDIT")
        }

        if (actionsSet.has("TOGGLE_PICKUP_DEMURRAGE")) {
          enabledActions.add("TOGGLE_PICKUP_DEMURRAGE")
        }

        if (actionsSet.has("TOGGLE_DELIVERY_DEMURRAGE")) {
          enabledActions.add("TOGGLE_DELIVERY_DEMURRAGE")
        }

        if (actionsSet.has("TOGGLE_DISPATCH_VALIDATION")) {
          enabledActions.add("TOGGLE_DISPATCH_VALIDATION")
        }
    }

    if (hasAccess("transporttransportassoc_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }

      if (actionsSet.has("DELETE_ASSOC")) {
        enabledActions.add("DELETE_ASSOC")
      }
    }

    if (hasAccess("transporttransportassoc_view")) {
      if (actionsSet.has("DOWNLOAD")) {
        enabledActions.add("DOWNLOAD")
      }
    }

    return enabledActions
  }, [ticket, actions, hasAccess])

  const {
    onTicketActionClick
  } = useTicketAction()

  const _onActionClick = useCallback((action: TicketAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onTicketActionClick(ticket, action)
    }
  }, [onActionClick, onTicketActionClick, ticket])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("TOGGLE_DISPATCH_VALIDATION") &&
        <Menu.Item
          onClick={() => _onActionClick("TOGGLE_DISPATCH_VALIDATION")}
          icon={<IconCircleCheck size={14}/>}>
          {ticket.dispatchValidatedDttm ? t("common.action.validate", "Invalidate") : t("common.action.invalidate", "Validate")}
        </Menu.Item>
      }
      {visibleActions.has("DOWNLOAD") &&
        <Menu.Item
          onClick={() => _onActionClick("DOWNLOAD")}
          icon={<IconDownload size={14}/>}>
          {t("common.action.download", "Download")}
        </Menu.Item>
      }
      {(visibleActions.has("TOGGLE_PICKUP_DEMURRAGE") || visibleActions.has("TOGGLE_DELIVERY_DEMURRAGE")) &&
        <>
          <Menu.Divider/>
          {visibleActions.has("TOGGLE_PICKUP_DEMURRAGE") &&
            <Menu.Item onClick={() => _onActionClick("TOGGLE_PICKUP_DEMURRAGE")} disabled={typeof ticket.pickupTransport === 'undefined'}>
              {t("common.entity.transport.type.pickup", "Pickup")}
              {": "}
              {ticket.pickupTransport?.disableDemurrage ? t("common.action.enable.demurrage", "Enable Demurrage") : t("common.action.disable.demurrage", "Disable Demurrage")}
            </Menu.Item>
          }
          {visibleActions.has("TOGGLE_DELIVERY_DEMURRAGE") &&
            <Menu.Item onClick={() => _onActionClick("TOGGLE_DELIVERY_DEMURRAGE")} disabled={typeof ticket.deliveryTransport === 'undefined'}>
              {t("common.entity.transport.type.delivery", "Delivery")}
              {": "}
              {ticket.deliveryTransport?.disableDemurrage ? t("common.action.enable.demurrage", "Enable Demurrage") : t("common.action.disable.demurrage", "Disable Demurrage")}
            </Menu.Item>
          }
        </>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
          {visibleActions.has("DELETE_ASSOC") &&
            <Menu.Item
              onClick={() => _onActionClick("DELETE_ASSOC")}
              icon={<IconTrash size={14}/>}
              color="red">
              {t("common.action.delete.association", "Delete Association")}
            </Menu.Item>
          }
        </>
      }
    </>
  )
}
